/**
 * Mobile Home - HAL 3000 Mobile Version
 * Style cockpit avion / centrale nucléaire
 * Interrupteurs DYMO + Températures géantes
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import CockpitToggle from '@/components/CockpitToggle';
import { 
  Lightbulb, Thermometer, Flame, Power, PowerOff, RefreshCw, Home,
  ChevronRight, ChevronUp, ChevronDown, Droplets, TrendingUp
} from 'lucide-react';

// Composant vis décorative
const Screw = ({ size = 8 }) => (
  <div 
    className="rounded-full"
    style={{
      width: size,
      height: size,
      background: 'linear-gradient(135deg, #888 0%, #444 50%, #666 100%)',
      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8), 0 1px 1px rgba(255,255,255,0.1)'
    }}
  >
    <div 
      className="w-full h-full flex items-center justify-center"
      style={{ transform: 'rotate(45deg)' }}
    >
      <div style={{ width: '60%', height: 1, background: '#333' }} />
    </div>
  </div>
);

// Étiquette style DYMO embossée
const DymoLabel = ({ text, active = false }) => (
  <div 
    className="px-1 py-0.5 rounded-sm text-center uppercase tracking-wide"
    style={{
      background: active ? '#1a1a1a' : '#0d0d0d',
      border: '1px solid #333',
      fontFamily: 'monospace',
      fontSize: '7px',
      fontWeight: 'bold',
      letterSpacing: '0.5px',
      color: active ? '#fff' : '#888',
      textShadow: active ? '0 0 4px rgba(255,255,255,0.3)' : 'none',
      lineHeight: '1.2',
      whiteSpace: 'pre-line',
      maxWidth: '50px'
    }}
  >
    {text}
  </div>
);

const MobileHome = ({ onSwitchToDesktop }) => {
  const [lights, setLights] = useState([]);
  const [heating, setHeating] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('lights');
  const [toggling, setToggling] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [historyPeriod, setHistoryPeriod] = useState('48h');

  const SAINT_GEORGES_OWNER_ID = 26773026;

  const loadData = useCallback(async () => {
    try {
      const [devicesRes, vicareRes] = await Promise.all([
        api.get('/integrations/tuya/devices'),
        api.get('/integrations/vicare/status')
      ]);

      if (devicesRes.data?.success) {
        const devices = devicesRes.data.devices || [];
        
        const lightCategories = ['cz', 'dj', 'dc', 'kg'];
        const excludeCategories = ['ywbj', 'pir', 'mcs', 'wsdcg', 'wk', 'qn', 'dlq', 'qccdz', 'tdq', 'wg2'];
        
        const lightDevices = devices
          .filter(d => {
            const cat = d.category || '';
            const name = (d.name || '').toLowerCase();
            const ownerId = Number(d.owner_id);
            
            if (ownerId !== SAINT_GEORGES_OWNER_ID) return false;
            if (excludeCategories.includes(cat)) return false;
            if (name.includes('capteur') || name.includes('détect') || name.includes('chauffage')) return false;
            
            return lightCategories.includes(cat) || 
              name.includes('lampe') || name.includes('guirlande') || 
              name.includes('plafond') || name.includes('éclairage');
          })
          .map(d => {
            let isOn = false;
            if (d.status && Array.isArray(d.status)) {
              const sw = d.status.find(s => s.code === 'switch_led' || s.code === 'switch_1');
              if (sw) isOn = sw.value === true;
            }
            return { id: d.id, name: d.name, online: d.online, isOn, source: 'tuya' };
          });
        
        const kasaDevices = [
          { id: 'kasa-boule-disco', name: 'Boule disco', online: true, isOn: false, source: 'kasa' },
          { id: 'kasa-lampes-piano', name: 'Lampes Piano', online: true, isOn: false, source: 'kasa' },
          { id: 'kasa-vitrine', name: 'Vitrine+secretaire', online: true, isOn: false, source: 'kasa' },
          { id: 'ewelink-guerison', name: 'Lampe guéridon', online: true, isOn: false, source: 'ewelink' }
        ];
        
        const savedState = JSON.parse(localStorage.getItem('hal_kasa_state') || '{}');
        kasaDevices.forEach(k => {
          if (savedState[k.id] !== undefined) k.isOn = savedState[k.id];
        });
        
        setLights([...lightDevices, ...kasaDevices]);
        
        const tempSensors = devices
          .filter(d => d.category === 'wsdcg' && Number(d.owner_id) === SAINT_GEORGES_OWNER_ID)
          .map(d => {
            let temp = null, humidity = null;
            if (d.status) {
              const t = d.status.find(s => s.code === 'va_temperature' || s.code === 'temp_current');
              const h = d.status.find(s => s.code === 'va_humidity' || s.code === 'humidity_value');
              if (t) temp = t.value / 10;
              if (h) humidity = h.value;
            }
            // Marquer Atelier/Garage comme extérieur
            const isExterior = d.name?.toLowerCase().includes('atelier') || d.name?.toLowerCase().includes('garage');
            return { id: d.id, name: d.name, online: d.online, temperature: temp, humidity, isExterior };
          })
          .filter(s => s.online && s.temperature !== null)
          .sort((a, b) => a.temperature - b.temperature);
        
        setSensors(tempSensors);
      }

      if (vicareRes.data?.success) {
        setHeating({
          current: vicareRes.data.current_temperature,
          target: vicareRes.data.target_temperature,
          mode: vicareRes.data.heating_mode,
          burnerActive: vicareRes.data.burner?.active,
          burnerModulation: vicareRes.data.burner?.modulation,
          dhwTemp: vicareRes.data.dhw_temperature
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, [loadData]);

  const toggleLight = async (light) => {
    if (!light.online || toggling) return;
    
    setToggling(light.id);
    const newState = !light.isOn;
    
    if (light.source === 'kasa' || light.source === 'ewelink') {
      const saved = JSON.parse(localStorage.getItem('hal_kasa_state') || '{}');
      saved[light.id] = newState;
      localStorage.setItem('hal_kasa_state', JSON.stringify(saved));
    } else {
      try {
        await api.post(`/integrations/tuya/devices/${light.id}/commands`, {
          commands: [{ code: 'switch_led', value: newState }]
        });
      } catch (e) {
        try {
          await api.post(`/integrations/tuya/devices/${light.id}/commands`, {
            commands: [{ code: 'switch_1', value: newState }]
          });
        } catch (e2) {}
      }
    }
    
    setLights(prev => prev.map(l => l.id === light.id ? { ...l, isOn: newState } : l));
    setToggling(null);
  };

  // Formatage nom DYMO court
  const formatDymoName = (name) => {
    return name
      .replace('Extension ', 'EXT\n')
      .replace('Guirlande ', 'GUIRL\n')
      .replace('guirlande ', 'GUIRL\n')
      .replace('Lampes ', '')
      .replace('Plafond ', 'PLAF\n')
      .replace('plafond ', 'PLAF\n')
      .replace('St Georges', 'ST.G')
      .replace('+secretaire', '\n+SEC')
      .replace('deco extension', 'DECO\nEXT')
      .replace('Boule disco', 'BOULE\nDISCO')
      .replace('Lampe guéridon', 'LAMPE\nGUERID')
      .replace('Vitrine', 'VITR')
      .replace('Piano', 'PIANO')
      .replace('terrasse', 'TERR')
      .replace('cerisier', 'CERIS')
      .replace('grange', 'GRANG')
      .replace('miroir', 'MIROIR')
      .replace('veranda', 'VERAND')
      .replace('Parking', 'PARK')
      .replace('salon', 'SALON')
      .toUpperCase();
  };

  // Calcul moyenne maison (sans extérieur)
  const interiorSensors = sensors.filter(s => !s.isExterior);
  const exteriorSensor = sensors.find(s => s.isExterior);
  const avgTemp = interiorSensors.length > 0 
    ? interiorSensors.reduce((sum, s) => sum + s.temperature, 0) / interiorSensors.length 
    : null;
  const avgHumidity = interiorSensors.filter(s => s.humidity).length > 0
    ? interiorSensors.filter(s => s.humidity).reduce((sum, s) => sum + s.humidity, 0) / interiorSensors.filter(s => s.humidity).length
    : null;

  const lightsOn = lights.filter(l => l.isOn).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-cyan-400 font-mono animate-pulse">INITIALISATION...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-14 overflow-hidden">
      {/* Header avec vis */}
      <div className="bg-gradient-to-b from-gray-900 to-black border-b-2 border-gray-700 p-2 flex items-center justify-between relative">
        <div className="absolute top-1 left-1"><Screw size={6} /></div>
        <div className="absolute top-1 right-1"><Screw size={6} /></div>
        <div className="absolute bottom-1 left-1"><Screw size={6} /></div>
        <div className="absolute bottom-1 right-1"><Screw size={6} /></div>
        
        <div className="flex items-center gap-2 ml-3">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-cyan-400 font-bold font-mono text-sm">HAL 3000</span>
        </div>
        <div className="flex items-center gap-2 mr-3">
          <span className="text-[10px] text-amber-400 font-mono">{lightsOn}/{lights.length}</span>
          <button onClick={loadData} className="p-1 bg-gray-800 rounded border border-gray-600">
            <RefreshCw className="w-3 h-3 text-cyan-400" />
          </button>
        </div>
      </div>

      {/* Tab Navigation style panneau */}
      <div className="flex bg-gradient-to-b from-gray-800 to-gray-900 border-b-2 border-gray-700 text-[10px] relative">
        <div className="absolute top-1 left-2"><Screw size={5} /></div>
        <div className="absolute top-1 right-2"><Screw size={5} /></div>
        
        {[
          { id: 'lights', label: 'ÉCLAIRAGES', icon: Lightbulb, color: 'yellow' },
          { id: 'temps', label: 'CAPTEURS', icon: Thermometer, color: 'cyan' },
          { id: 'heating', label: 'CHAUFF.', icon: Flame, color: 'orange' }
        ].map(tab => (
          <button 
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 font-mono border-r border-gray-700 last:border-0 ${
              activeTab === tab.id 
                ? `text-${tab.color}-400 bg-black/50` 
                : 'text-gray-500'
            }`}
          >
            <tab.icon className={`w-4 h-4 mx-auto mb-0.5 ${activeTab === tab.id ? `text-${tab.color}-400` : ''}`} />
            <DymoLabel text={tab.label} active={activeTab === tab.id} />
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="h-[calc(100vh-140px)] overflow-hidden">
        
        {/* === ÉCLAIRAGES === */}
        {activeTab === 'lights' && (
          <div className="h-full flex flex-col bg-gradient-to-b from-gray-900 to-black p-2">
            {/* Quick Actions */}
            <div className="flex gap-2 mb-2">
              <button 
                onClick={async () => {
                  for (const l of lights.filter(x => x.online && x.source === 'tuya' && !x.isOn)) {
                    await toggleLight(l);
                  }
                }}
                className="flex-1 py-2 bg-green-900/80 rounded border-2 border-green-600 font-bold text-[10px] flex items-center justify-center gap-1 font-mono"
              >
                <Power className="w-3 h-3" /> TOUT ON
              </button>
              <button 
                onClick={async () => {
                  for (const l of lights.filter(x => x.online && x.source === 'tuya' && x.isOn)) {
                    await toggleLight(l);
                  }
                }}
                className="flex-1 py-2 bg-red-900/80 rounded border-2 border-red-600 font-bold text-[10px] flex items-center justify-center gap-1 font-mono"
              >
                <PowerOff className="w-3 h-3" /> TOUT OFF
              </button>
            </div>
            
            {/* Panneau de contrôle avec vis */}
            <div 
              className="flex-1 rounded-lg p-2 relative overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)',
                border: '3px solid #333',
                boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.8)'
              }}
            >
              {/* Vis aux coins */}
              <div className="absolute top-2 left-2"><Screw size={8} /></div>
              <div className="absolute top-2 right-2"><Screw size={8} /></div>
              <div className="absolute bottom-2 left-2"><Screw size={8} /></div>
              <div className="absolute bottom-2 right-2"><Screw size={8} /></div>
              
              {/* Grille d'interrupteurs centrée */}
              <div 
                className="h-full flex flex-wrap justify-center items-start content-start gap-1 py-2 overflow-hidden"
              >
                {lights.map(light => (
                  <div 
                    key={light.id}
                    className={`flex flex-col items-center p-1 ${!light.online ? 'opacity-30' : ''}`}
                    style={{ width: '48px' }}
                  >
                    <button
                      onClick={() => toggleLight(light)}
                      disabled={!light.online || toggling === light.id}
                      className="focus:outline-none transform hover:scale-105 transition-transform"
                    >
                      <CockpitToggle
                        isOn={light.isOn}
                        label=""
                        disabled={!light.online || toggling === light.id}
                        size="mini"
                      />
                    </button>
                    
                    <DymoLabel text={formatDymoName(light.name)} active={light.isOn} />
                    
                    {light.source !== 'tuya' && (
                      <div 
                        className="mt-0.5 px-1 rounded text-white font-mono"
                        style={{ 
                          fontSize: '5px',
                          background: light.source === 'kasa' ? '#166534' : '#7c3aed'
                        }}
                      >
                        {light.source.toUpperCase()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* === CAPTEURS TEMPÉRATURE === */}
        {activeTab === 'temps' && (
          <div className="h-full flex flex-col bg-gradient-to-b from-gray-900 to-black p-2 overflow-auto">
            
            {/* MOYENNE MAISON - TRÈS GROS */}
            <div 
              className="rounded-lg p-3 mb-2 relative"
              style={{
                background: 'linear-gradient(180deg, #0a2010 0%, #051008 100%)',
                border: '3px solid #22c55e',
                boxShadow: '0 0 20px rgba(34, 197, 94, 0.3), inset 0 2px 10px rgba(0,0,0,0.8)'
              }}
            >
              <div className="absolute top-1 left-1"><Screw size={6} /></div>
              <div className="absolute top-1 right-1"><Screw size={6} /></div>
              
              <DymoLabel text="MOYENNE MAISON" active />
              
              <div className="flex items-center justify-center gap-6 mt-2">
                {/* Température moyenne */}
                <div className="text-center">
                  <div 
                    className="text-5xl font-bold font-mono"
                    style={{ 
                      color: '#4ade80',
                      textShadow: '0 0 20px rgba(74, 222, 128, 0.8)'
                    }}
                  >
                    {avgTemp?.toFixed(1) || '--'}°
                  </div>
                  <DymoLabel text="TEMP" active />
                </div>
                
                {/* Séparateur */}
                <div className="w-px h-16 bg-green-800" />
                
                {/* Humidité moyenne */}
                <div className="text-center">
                  <div 
                    className="text-5xl font-bold font-mono"
                    style={{ 
                      color: '#22d3ee',
                      textShadow: '0 0 20px rgba(34, 211, 238, 0.8)'
                    }}
                  >
                    {avgHumidity?.toFixed(0) || '--'}%
                  </div>
                  <DymoLabel text="HUMID" active />
                </div>
              </div>
              
              {/* Cibles chauffage */}
              <div className="flex justify-center gap-4 mt-3 pt-2 border-t border-green-900">
                <div className="text-center">
                  <div className="text-lg font-bold text-orange-400 font-mono">
                    {heating?.target?.toFixed(1) || '--'}°
                  </div>
                  <div className="text-[8px] text-gray-400">CIBLE CHAUD.</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-amber-400 font-mono">--°</div>
                  <div className="text-[8px] text-gray-400">CIBLE POÊLE</div>
                </div>
              </div>
            </div>
            
            {/* EXTÉRIEUR - Différencié */}
            {exteriorSensor && (
              <div 
                className="rounded-lg p-2 mb-2 flex items-center justify-between"
                style={{
                  background: 'linear-gradient(180deg, #1e3a5f 0%, #0c1929 100%)',
                  border: '2px solid #3b82f6'
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <DymoLabel text="EXTÉRIEUR" active />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-blue-400 font-mono">
                    {exteriorSensor.temperature?.toFixed(1)}°
                  </span>
                  {exteriorSensor.humidity && (
                    <span className="text-lg text-cyan-400 font-mono">
                      {exteriorSensor.humidity}%
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Liste capteurs intérieurs */}
            <div className="space-y-1 flex-1 overflow-auto">
              {interiorSensors.map(sensor => (
                <button 
                  key={sensor.id}
                  onClick={() => setSelectedSensor(sensor.id === selectedSensor ? null : sensor.id)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg border-2 transition-all ${
                    selectedSensor === sensor.id 
                      ? 'bg-green-900/30 border-green-500' 
                      : 'bg-gray-900/50 border-gray-700'
                  }`}
                >
                  <DymoLabel text={sensor.name.toUpperCase()} active={selectedSensor === sensor.id} />
                  <div className="flex items-center gap-2">
                    <span className={`text-2xl font-bold font-mono ${
                      sensor.temperature < 15 ? 'text-blue-400' :
                      sensor.temperature < 18 ? 'text-cyan-400' :
                      sensor.temperature < 20 ? 'text-green-400' :
                      'text-orange-400'
                    }`}>
                      {sensor.temperature.toFixed(1)}°
                    </span>
                    {sensor.humidity && (
                      <span className="text-lg text-cyan-400/70 font-mono">
                        {sensor.humidity}%
                      </span>
                    )}
                    <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${
                      selectedSensor === sensor.id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </button>
              ))}
            </div>
            
            {/* Modal historique */}
            {selectedSensor && (
              <div className="mt-2 p-2 bg-gray-900 rounded-lg border border-gray-700">
                <div className="flex gap-1 mb-2">
                  {['48h', '7j', '1m', '1a'].map(p => (
                    <button 
                      key={p}
                      onClick={() => setHistoryPeriod(p)}
                      className={`flex-1 py-1 text-[10px] font-mono rounded ${
                        historyPeriod === p ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-400'
                      }`}
                    >
                      {p.toUpperCase()}
                    </button>
                  ))}
                </div>
                <div className="h-16 bg-black/50 rounded flex items-center justify-center text-gray-500 text-xs">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  HISTORIQUE {historyPeriod.toUpperCase()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === CHAUFFAGE === */}
        {activeTab === 'heating' && heating && (
          <div className="h-full p-2 space-y-2 overflow-auto bg-gradient-to-b from-gray-900 to-black">
            <div 
              className="rounded-lg p-3 relative"
              style={{
                background: 'linear-gradient(180deg, #2d1810 0%, #1a0a05 100%)',
                border: '3px solid #f97316',
                boxShadow: '0 0 20px rgba(249, 115, 22, 0.3)'
              }}
            >
              <div className="absolute top-1 left-1"><Screw size={6} /></div>
              <div className="absolute top-1 right-1"><Screw size={6} /></div>
              
              <DymoLabel text="VIESSMANN" active />
              
              <div className="text-center my-3">
                <div 
                  className="text-6xl font-bold font-mono"
                  style={{ color: '#fb923c', textShadow: '0 0 20px rgba(251, 146, 60, 0.8)' }}
                >
                  {heating.current?.toFixed(1) || '--'}°
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-4">
                <button 
                  onClick={async () => {
                    const newTarget = (heating.target || 20) - 0.5;
                    await api.post('/integrations/vicare/temperature', { temperature: newTarget });
                    setHeating(prev => ({ ...prev, target: newTarget }));
                  }}
                  className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center border-2 border-blue-400"
                >
                  <ChevronDown className="w-6 h-6" />
                </button>
                
                <div className="text-center">
                  <div className="text-3xl font-bold text-white font-mono">
                    {heating.target?.toFixed(1) || '--'}°
                  </div>
                  <DymoLabel text="CIBLE" active />
                </div>
                
                <button 
                  onClick={async () => {
                    const newTarget = (heating.target || 20) + 0.5;
                    await api.post('/integrations/vicare/temperature', { temperature: newTarget });
                    setHeating(prev => ({ ...prev, target: newTarget }));
                  }}
                  className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center border-2 border-red-400"
                >
                  <ChevronUp className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className={`p-2 rounded-lg border-2 flex items-center justify-between ${
              heating.burnerActive ? 'bg-orange-900/30 border-orange-500' : 'bg-gray-800/50 border-gray-600'
            }`}>
              <div className="flex items-center gap-2">
                <Flame className={`w-5 h-5 ${heating.burnerActive ? 'text-orange-400' : 'text-gray-500'}`} />
                <DymoLabel text="BRÛLEUR" active={heating.burnerActive} />
              </div>
              <span className={`text-xl font-bold font-mono ${heating.burnerActive ? 'text-orange-400' : 'text-gray-500'}`}>
                {heating.burnerActive ? `${heating.burnerModulation || 0}%` : 'OFF'}
              </span>
            </div>
            
            <div className="p-2 rounded-lg border-2 bg-blue-900/30 border-blue-500 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Droplets className="w-5 h-5 text-blue-400" />
                <DymoLabel text="EAU CHAUDE" active />
              </div>
              <span className="text-xl font-bold text-blue-400 font-mono">
                {heating.dhwTemp?.toFixed(0) || '--'}°
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t-2 border-gray-700 p-1">
        <button 
          onClick={onSwitchToDesktop}
          className="w-full py-1 bg-gray-800 rounded text-gray-400 text-[10px] font-mono flex items-center justify-center gap-1 border border-gray-600"
        >
          VERSION TABLETTE <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default MobileHome;
